import { useParams } from "react-router-dom";
import { PublicNav } from "@/components/PublicNav";
import { Footer } from "@/components/Footer";
import { AlertTriangle } from "lucide-react";

const CONTENT = {
  about: {
    title: "About ManaJodi",
    intro: "ManaJodi is a modern Indian matrimonial platform built on trust, technology and family values.",
    sections: [
      ["Our mission", "We help individuals and families discover compatible life-partner profiles while keeping users fully in control of their decisions. We combine traditional matrimony with modern search, AI compatibility and strong safety."],
      ["What makes us different", "Verified profiles, informational AI compatibility, granular privacy, and optional Family Connect — all in one warm, respectful experience."],
      ["Powered by", "ManaJodi is powered by Vijay Software Solutions Pvt Ltd."],
    ],
  },
  "how-it-works": {
    title: "How ManaJodi Works",
    intro: "Finding your Jodi is a guided, four-step journey — and you stay in control at every step.",
    sections: [
      ["1. Create your profile", "Register with your mobile number, verify via OTP and build a rich profile — self, parent or family managed."],
      ["2. Discover matches", "Use smart search, quick filters and preference-based matching to find compatible profiles. No random results."],
      ["3. Understand compatibility", "ManaJodi AI explains where you align across lifestyle, career, family, location and interests, and suggests topics to discuss."],
      ["4. Connect safely", "Send interests, chat securely, request photos or contact, and involve family when you're ready. Privacy is always respected."],
    ],
  },
  safety: {
    title: "Safety Guidelines",
    intro: "Your safety is our priority. Please read and follow these guidelines.",
    warn: true,
    sections: [
      ["Never send money", "Never send money or share financial information with anyone you meet online, regardless of the reason given."],
      ["Protect your information", "Keep phone numbers, addresses and financial details private until you are confident and comfortable."],
      ["Meet safely", "Meet in public places, tell a trusted person, and use in-platform communication initially."],
      ["Report suspicious activity", "Use Block & Report on any profile that seems fake, abusive or asks for money. Our safety team reviews every report confidentially."],
      ["Verification is not a guarantee", "Verification confirms specific details were checked. It does not guarantee a person's behaviour. Always verify important information independently."],
    ],
  },
  faq: {
    title: "Frequently Asked Questions",
    intro: "Answers to common questions about ManaJodi.",
    sections: [
      ["Is ManaJodi free?", "Yes. You can create a profile, search, send interests and chat for free. Premium (₹150/month) and Lifetime (₹2,000) unlock advanced features."],
      ["How does AI compatibility work?", "Our AI compares your profile and preferences with a match to show alignment across key areas. It is informational only and never predicts marriage success."],
      ["Who can see my photos?", "You control this. Private photos are only visible after you approve a photo request or connect with someone."],
      ["What is Family Connect?", "You can invite family members with limited, controlled access to help in your search. Private chats are never shared automatically."],
      ["How do I cancel Premium?", "You can cancel anytime from Settings → Membership. Lifetime membership is permanent and non-cancellable."],
    ],
  },
  contact: {
    title: "Contact Us",
    intro: "We're here to help. Reach our support team any time.",
    sections: [
      ["Support email", "For any help, email us at info@vs-solutions.org — our team responds within 24–48 hours."],
      ["Support", "Logged-in users can raise a ticket from Safety Center → Contact Support for account, payment or safety help."],
      ["Company", "Vijay Software Solutions Pvt Ltd."],
    ],
  },
  terms: { title: "Terms & Conditions", intro: "By using ManaJodi you agree to these terms.", sections: [["User-generated content", "Profiles are created by users. ManaJodi does not guarantee the accuracy of any profile."], ["Acceptable use", "You agree not to misuse the platform, harass others, or engage in fraudulent activity."], ["AI compatibility", "AI compatibility is informational and must not be treated as a prediction of relationship or marriage success."]] },
  privacy: { title: "Privacy Policy", intro: "How we handle your data.", sections: [["Your data", "We collect only what is needed to provide the service and protect your privacy with granular controls."], ["Private information", "Phone, email, private photos, income and exact location are protected and shown only per your privacy settings."], ["Search engines", "Private user profiles are never exposed to search engines."]] },
  community: { title: "Community Guidelines", intro: "Keeping ManaJodi respectful and safe.", sections: [["Be respectful", "Treat every member with courtesy. Harassment and abuse are not tolerated."], ["Be genuine", "Use real information and your own photos. Fake profiles are removed."], ["Report concerns", "Help us keep the community safe by reporting suspicious behaviour."]] },
  refund: { title: "Refund Policy", intro: "Our approach to refunds.", sections: [["Subscriptions", "Premium subscriptions renew monthly and can be cancelled anytime; access continues until the end of the paid period."], ["Lifetime", "Lifetime membership is a one-time payment and is generally non-refundable except as required by law."], ["Requests", "Refund requests are handled via Support and processed per applicable regulations."]] },
};

export default function StaticPage({ slugOverride }) {
  const params = useParams();
  const slug = slugOverride || params.slug || "about";
  const c = CONTENT[slug] || CONTENT.about;
  return (
    <div className="bg-[#FDFBF7] min-h-screen" data-testid={`static-${slug}`}>
      <PublicNav />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <h1 className="font-serif text-4xl font-bold text-[#58111A]">{c.title}</h1>
        <p className="text-[#605255] mt-3 text-lg">{c.intro}</p>
        {c.warn && (
          <div className="mt-6 flex items-start gap-3 rounded-xl bg-[#FEF2F2] border border-[#FECACA] p-4 text-sm text-[#991B1B]">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <span><strong>Never send money to someone you met online.</strong> Report any request for money immediately.</span>
          </div>
        )}
        <div className="mt-8 space-y-7">
          {c.sections.map(([h, b]) => (
            <div key={h}>
              <h2 className="font-serif text-xl font-semibold text-[#261A1C]">{h}</h2>
              <p className="text-[#605255] mt-2 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </article>
      <Footer />
    </div>
  );
}
